const resourcePaginationStates = new Map();
const transitionDelay = (duration) => new Promise((resolve) => setTimeout(resolve, duration));
const pageValue = (url, section) => new URL(url, window.location.href).searchParams.get(section.dataset.resourcePageName) || '1';

const loadResourcePage = async (section, url, pushHistory) => {
  const state = resourcePaginationStates.get(section);
  const results = section.querySelector('[data-resource-results]');
  if (!state || !results) return;

  state.controller?.abort();
  const controller = new AbortController();
  state.controller = controller;
  results.classList.remove('is-changing');
  results.style.minHeight = '';
  section.setAttribute('aria-busy', 'true');
  const isCurrent = () => state.controller === controller && !controller.signal.aborted;
  const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180;

  try {
    const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Resource page unavailable');
    const html = await response.text();
    if (!isCurrent()) return;
    const nextSection = new DOMParser().parseFromString(html, 'text/html').getElementById(section.id);
    const nextResults = nextSection?.querySelector('[data-resource-results]');
    if (!nextResults) throw new Error('Resource results unavailable');

    const restoreFocus = results.contains(document.activeElement);
    results.style.minHeight = `${results.getBoundingClientRect().height}px`;
    results.classList.add('is-changing');
    if (duration) await transitionDelay(duration);
    if (!isCurrent()) return;
    results.innerHTML = nextResults.innerHTML;
    window.initBtnRoll?.(results);
    window.initDataTilt?.(results)?.catch?.(() => {});
    if (pushHistory) window.history.pushState(null, '', url);
    state.url = url;
    results.classList.remove('is-changing');
    if (duration) await transitionDelay(duration);
    if (!isCurrent()) return;
    results.style.minHeight = '';
    if (restoreFocus) results.focus({ preventScroll: true });
    const header = document.querySelector('.header');
    const offset = (header?.getBoundingClientRect().height || 0) + 16;
    window.scrollTo({ top: Math.max(0, section.getBoundingClientRect().top + window.scrollY - offset), behavior: duration ? 'smooth' : 'instant' });
  } catch (error) {
    if (isCurrent() && error.name !== 'AbortError') window.location.assign(url);
  } finally {
    if (state.controller === controller) {
      results.classList.remove('is-changing');
      results.style.minHeight = '';
      section.removeAttribute('aria-busy');
      state.controller = null;
    }
  }
};

const initResourcePagination = (scope = document) => {
  if (typeof window.fetch !== 'function' || typeof AbortController === 'undefined') return;
  const sections = [...(scope.querySelectorAll?.('[data-resource-pagination]') || [])];
  if (scope.matches?.('[data-resource-pagination]')) sections.unshift(scope);

  sections.forEach((section) => {
    if (resourcePaginationStates.has(section) || !section.id || !section.querySelector('[data-resource-results]')) return;
    resourcePaginationStates.set(section, { url: window.location.href, controller: null });
    section.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('.resources-sec__pagination a');
      if (!link || !section.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      event.preventDefault();
      loadResourcePage(section, url.href, true);
    });
  });
};

window.addEventListener('popstate', () => {
  resourcePaginationStates.forEach((state, section) => {
    if (!section.isConnected) {
      state.controller?.abort();
      resourcePaginationStates.delete(section);
      return;
    }
    if (state.controller || pageValue(state.url, section) !== pageValue(window.location.href, section)) {
      loadResourcePage(section, window.location.href, false);
    }
  });
});

initResourcePagination();
window.initResourcePagination = initResourcePagination;
